import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { useIsSolo } from "@/hooks/useIsSolo";
import { supabase } from "@/integrations/supabase/client";
import { fetchAccounts, fetchCategories, fetchTransactionsWithLines, fetchMembers, monthRange } from "@/lib/queries";
import { fetchCcPurchasesRange, fetchCcDebtByAccountScoped } from "@/lib/cc";
import { accountsQueryKey, ccDebtByAccountQueryKey } from "@/lib/authScopedQueryKeys";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES } from "@/lib/currencies";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { accountScope } from "@/lib/accountDisplay";
import { buildEffectiveBudgetMatrix, filterBudgetsByScope, findYtdStartMonth, projectedAnnualBudget, totalEffectiveForMonth } from "@/lib/effectiveBudget";
import { ArrowDownRight, ArrowUpRight, Wallet, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LabelList } from "recharts";
import { useMemo, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { consumePendingSignupIntent } from "@/lib/pendingSignupIntent";
import { BudgetHygieneBanner } from "@/components/BudgetHygieneBanner";
import { AdjustmentsBanner } from "@/components/AdjustmentsBanner";
import { UncategorizedBanner } from "@/components/UncategorizedBanner";
import { isAdjustCategory } from "@/lib/categories";
import { InviteMoveDialog } from "@/components/InviteMoveDialog";
import { useAuth } from "@/hooks/useAuth";
import { HouseholdFairShareSummary } from "@/components/HouseholdFairShareCard";
import { formatMonthStart } from "@/components/MonthPicker";

export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardPage,
});

// Determina si una compra CC es visible según el modo Hogar/Personal,
// usando las cuotas (cc_charges) como fuente de verdad de privacidad.
function ccPurchaseVisible(
  charges: any[],
  viewMode: "household" | "personal",
  myId: string | undefined,
  isSolo: boolean,
): boolean {
  if (isSolo) return true;
  if (viewMode === "household") return charges.some((c) => c.visibility === "shared");
  return charges.some((c) => c.visibility === "private" && c.owner_id === myId);
}

function buildCcChargesMap(charges: any[]): Map<string, any[]> {
  const m = new Map<string, any[]>();
  charges.forEach((c) => {
    const arr = m.get(c.purchase_id) ?? [];
    arr.push(c);
    m.set(c.purchase_id, arr);
  });
  return m;
}

function DashboardPage() {
  const formatCurrency = useFormatCurrency();
  const { profile } = useProfile();
  const { user } = useAuth();
  const { year, month, viewMode, activeCurrency, setActiveCurrency } = useAppStore();
  const { isSolo } = useIsSolo();
  const hh = profile?.household_id;
  const queryClient = useQueryClient();
  const intentApplied = useRef(false);
  const [inviteMove, setInviteMove] = useState<{ name: string; code: string } | null>(null);

  useEffect(() => {
    if (!profile || !user || intentApplied.current) return;

    const intent = consumePendingSignupIntent();
    // Fuente primaria: user_metadata (server-side, sobrevive PKCE cross-tab)
    let pendingCode: string | null = (user.user_metadata as any)?.pending_invite_code ?? null;
    // Fallback: localStorage (para usuarios registrados antes de este fix)
    if (!pendingCode) {
      try { pendingCode = localStorage.getItem("pendingInviteCode"); } catch {}
    }

    if (!intent && !pendingCode) return;
    intentApplied.current = true;

    // Flujo email/contraseña con confirmación de email: el usuario llega al
    // dashboard directamente desde el link de confirmación, sin pasar por
    // handleSignIn(), por lo que pendingInviteCode nunca se consumiría ahí.
    if (!intent && pendingCode) {
      try { localStorage.removeItem("pendingInviteCode"); } catch {}
      (async () => {
        const { error: joinErr } = await supabase.rpc("join_household_by_code", {
          _code: pendingCode as string,
        });
        // Limpiar metadata independientemente del resultado
        try { await supabase.auth.updateUser({ data: { pending_invite_code: null } }); } catch {}
        if (joinErr && !joinErr.message.toLowerCase().includes("already in household")) {
          toast.error("No se pudo unir al hogar.");
        } else if (!joinErr) {
          toast.success("Te uniste al hogar.");
          await queryClient.invalidateQueries();
        }
      })();
      return;
    }

    // Flujo Google OAuth (pendingSignupIntent)
    // Limpiar pendingInviteCode colgante si existiera de un signup anterior
    try { localStorage.removeItem("pendingInviteCode"); } catch {}

    // Defensa en profundidad: si el usuario es claramente viejo,
    // descartamos sin llamar al server.
    const userCreatedAt = user.created_at ? new Date(user.created_at).getTime() : 0;
    if (userCreatedAt && Date.now() - userCreatedAt > 5 * 60 * 1000) {
      toast.info("Iniciaste sesión correctamente.");
      return;
    }

    (async () => {
      try {
        const payload =
          intent!.kind === "invite"
            ? { code: intent!.code, displayName: intent!.displayName }
            : {
                householdName: intent!.householdName,
                baseCurrency: intent!.baseCurrency,
                displayName: intent!.displayName,
              };
        const { data, error } = await supabase.rpc("apply_signup_intent", {
          _kind: intent!.kind,
          _payload: payload as any,
        });
        if (error) {
          toast.error("No pudimos aplicar tu configuración inicial.");
          return;
        }
        const status = (data as any)?.status as string | undefined;
        if (status === "applied") {
          toast.success(intent!.kind === "invite" ? "Te uniste al hogar." : "Hogar configurado.");
          await queryClient.invalidateQueries();
        } else if (status === "skipped_existing_user") {
          toast.info("Ya tenías una cuenta. No modificamos tu hogar actual.");
        } else if (status === "invite_offer_move" && intent!.kind === "invite") {
          setInviteMove({
            name: (data as any).target_household_name ?? "el hogar invitado",
            code: intent!.code,
          });
        } else if (status === "invalid_code") {
          toast.error("El código de invitación es inválido.");
        } else if (status === "household_full") {
          toast.error("Ese hogar ya está completo (máx. 2 personas).");
        }
      } catch {
        toast.error("Ocurrió un error aplicando tu configuración inicial.");
      }
    })();
  }, [profile, user, queryClient]);

  const profileId = profile?.id;
  const accountsQ = useQuery({ queryKey: accountsQueryKey(hh ?? "", profileId ?? ""), queryFn: () => fetchAccounts(hh!), enabled: !!hh && !!profileId });
  const categoriesQ = useQuery({ queryKey: ["categories", hh], queryFn: () => fetchCategories(hh!), enabled: !!hh });
  const membersQ = useQuery({ queryKey: ["members", hh], queryFn: () => fetchMembers(hh!), enabled: !!hh });
  const txQ = useQuery({
    queryKey: ["tx", hh, year, month],
    queryFn: () => fetchTransactionsWithLines(hh!, monthRange(year, month)),
    enabled: !!hh,
  });

  // Cargamos TODOS los presupuestos del hogar: necesitamos filas de años
  // anteriores para heredar correctamente el efectivo de cada mes del año
  // en curso (modelo escalonado "set it and forget it").
  const budgetsAllQ = useQuery({
    queryKey: ["budgets-all", hh],
    queryFn: async () => {
      const { data } = await supabase.from("budgets").select("*").eq("household_id", hh!);
      return data ?? [];
    },
    enabled: !!hh,
  });

  const householdQ = useQuery({
    queryKey: ["household", hh],
    queryFn: async () => (await supabase.from("households").select("*").eq("id", hh!).maybeSingle()).data,
    enabled: !!hh,
  });

  const ytdTxQ = useQuery({
    queryKey: ["tx-ytd", hh, year, month],
    queryFn: () => fetchTransactionsWithLines(hh!, { startDate: `${year}-01-01`, endDate: monthRange(year, month).endDate }),
    enabled: !!hh,
  });

  // Full year tx for annual chart
  const yearTxQ = useQuery({
    queryKey: ["tx-year", hh, year],
    queryFn: () => fetchTransactionsWithLines(hh!, { startDate: `${year}-01-01`, endDate: `${year}-12-31` }),
    enabled: !!hh,
  });

  // CC purchases del mes actual (para KPIs mensuales y gráfico de categorías)
  const ccQ = useQuery({
    queryKey: ["cc-purchases", hh, year, month],
    queryFn: () => fetchCcPurchasesRange(hh!, monthRange(year, month)),
    enabled: !!hh,
  });
  // CC purchases del año completo (para YTD y gráfico anual)
  const ccYearQ = useQuery({
    queryKey: ["cc-purchases-year", hh, year],
    queryFn: () => fetchCcPurchasesRange(hh!, { startDate: `${year}-01-01`, endDate: `${year}-12-31` }),
    enabled: !!hh,
  });

  // Deuda real de TC por cuenta (fuente de verdad: cc_charges pendientes).
  // accounts.balance quedó OBSOLETO para tarjetas tras el motor de TC: solo
  // acumula pagos, nunca la deuda de las compras. Usamos la misma vista
  // scoped que /tarjetas y /cuentas para que los números coincidan.
  const ccDebtScope: "household" | "personal" =
    isSolo ? "household" : (viewMode === "personal" ? "personal" : "household");
  const ccDebtQ = useQuery({
    queryKey: ccDebtByAccountQueryKey(hh ?? "", profileId ?? "", ccDebtScope),
    queryFn: () => fetchCcDebtByAccountScoped(ccDebtScope),
    enabled: !!hh && !!profileId,
  });

  const accounts = accountsQ.data ?? [];
  const categories = categoriesQ.data ?? [];
  const members = membersQ.data ?? [];
  const tx = txQ.data ?? { headers: [], lines: [] };

  const catMap = new Map(categories.map((c) => [c.id, c]));
  // IDs de las categorías "Ajuste de Saldo (+/−)". Se excluyen de KPIs y
  // gráficos principales para no distorsionar ingresos/gastos reales.
  const adjustIds = useMemo(
    () => new Set(categories.filter((c) => isAdjustCategory(c)).map((c) => c.id)),
    [categories],
  );
  const isAdjustLine = (l: any) => adjustIds.has(l.category_id ?? "");
  const headerById = useMemo(() => new Map(tx.headers.map((h) => [h.id, h])), [tx.headers]);
  const ytdHeaderById = useMemo(() => new Map((ytdTxQ.data?.headers ?? []).map((h) => [h.id, h])), [ytdTxQ.data?.headers]);
  const isRefundLine = (l: any, hMap: Map<string, any>) => !!(hMap.get(l.header_id) as any)?.is_refund;

  // Mapas de cuotas CC para lookup O(1)
  const ccChargesMap = useMemo(() => buildCcChargesMap(ccQ.data?.charges ?? []), [ccQ.data]);
  const ccYearChargesMap = useMemo(() => buildCcChargesMap(ccYearQ.data?.charges ?? []), [ccYearQ.data]);

  // Cuentas filtradas por scope (Hogar = compartidas, Personal = mías).
  const scopedAccounts = useMemo(() => {
    if (isSolo) return accounts;
    return accounts.filter((a) => accountScope(a as any, profile?.id, viewMode));
  }, [accounts, viewMode, profile?.id, isSolo]);

  const filteredLines = useMemo(() => {
    if (isSolo) return tx.lines;
    if (viewMode === "personal") {
      return tx.lines.filter((l) => l.visibility === "private" && l.owner_id === profile?.id);
    }
    return tx.lines.filter((l) => l.visibility === "shared");
  }, [tx.lines, viewMode, profile?.id, isSolo]);

  // Deuda de TC: NO usar accounts.balance (obsoleto para tarjetas). La fuente
  // de verdad son las cuotas pendientes (cc_charges) vía la vista scoped.
  // OJO: NO filtrar por scopedAccounts. En modo Personal, mi deuda privada
  // puede vivir en una tarjeta del HOGAR (ej. BBVA compartida): esa tarjeta no
  // está en scopedAccounts, pero el RPC scoped ya devuelve mi porción privada.
  // Sumamos el mapa completo (mismo criterio que la página /tarjetas).
  const cardDebt = Array.from(ccDebtQ.data?.values() ?? [])
    .reduce((s, r) => s + Number(r.debt), 0);
  // Patrimonio neto = activos líquidos (cuentas no-TC) − deuda real de TC.
  const liquidWorth = scopedAccounts
    .filter((a) => a.type !== "credit_card")
    .reduce((s, a) => s + Number(a.balance), 0);
  const netWorth = liquidWorth - cardDebt;

  const income = filteredLines.reduce((s, l) => {
    const c = catMap.get(l.category_id ?? "");
    if (isAdjustLine(l)) return s;
    return c?.type === "income" ? s + Number(l.amount) : s;
  }, 0);
  const regularExpenses = filteredLines.reduce((s, l) => {
    const c = catMap.get(l.category_id ?? "");
    if (c?.type !== "expense") return s;
    if (isAdjustLine(l)) return s;
    const sign = isRefundLine(l, headerById) ? -1 : 1;
    return s + sign * Number(l.amount);
  }, 0);
  const ccExpenses = (ccQ.data?.purchases ?? []).reduce((s, p) => {
    const ch = ccChargesMap.get(p.id) ?? [];
    if (!ccPurchaseVisible(ch, viewMode, profile?.id, isSolo)) return s;
    return s + Number(p.total_amount);
  }, 0);
  const expenses = regularExpenses + ccExpenses;

  const expenseByCat = useMemo(() => {
    // Si la categoría tiene parent_id, acumular en el padre para no
    // mostrar subcategorías sueltas en los gráficos del dashboard.
    const resolveRoot = (catId: string) => {
      const c = catMap.get(catId);
      if (!c) return null;
      if (c.parent_id) return catMap.get(c.parent_id) ?? c;
      return c;
    };

    const map = new Map<string, { name: string; value: number; color: string }>();
    filteredLines.forEach((l) => {
      const c = catMap.get(l.category_id ?? "");
      if (c?.type !== "expense") return;
      if (isAdjustLine(l)) return;
      const root = resolveRoot(c.id);
      if (!root) return;
      const cur = map.get(root.id) ?? { name: root.name, value: 0, color: root.color };
      const sign = isRefundLine(l, headerById) ? -1 : 1;
      cur.value += sign * Number(l.amount);
      map.set(root.id, cur);
    });
    // Agregar compras CC del mes
    (ccQ.data?.purchases ?? []).forEach((p) => {
      const ch = ccChargesMap.get(p.id) ?? [];
      if (!ccPurchaseVisible(ch, viewMode, profile?.id, isSolo)) return;
      if (!p.category_id) return;
      const c = catMap.get(p.category_id);
      if (!c || c.type !== "expense") return;
      const root = resolveRoot(c.id);
      if (!root) return;
      const cur = map.get(root.id) ?? { name: root.name, value: 0, color: root.color };
      cur.value += Number(p.total_amount);
      map.set(root.id, cur);
    });
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .filter((e) => e.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [filteredLines, catMap, headerById, adjustIds, ccQ.data, ccChargesMap, viewMode, profile?.id, isSolo]);

  // Neto de ajustes del mes (solo scope visible). + para "Ajuste de Saldo (+)"
  // (income), − para "Ajuste de Saldo (−)" (expense).
  const adjustmentsNet = useMemo(() => {
    let net = 0;
    let count = 0;
    for (const l of filteredLines) {
      if (!isAdjustLine(l)) continue;
      const c = catMap.get(l.category_id ?? "");
      if (!c) continue;
      const amount = Number(l.amount);
      net += c.type === "income" ? amount : -amount;
      count++;
    }
    return { net, count };
  }, [filteredLines, catMap, adjustIds]);

  // YTD
  const ytdLinesAll = ytdTxQ.data?.lines ?? [];
  const ytdLines = useMemo(() => {
    if (isSolo) return ytdLinesAll;
    if (viewMode === "personal") return ytdLinesAll.filter((l) => l.visibility === "private" && l.owner_id === profile?.id);
    return ytdLinesAll.filter((l) => l.visibility === "shared");
  }, [ytdLinesAll, viewMode, profile?.id, isSolo]);

  // Matriz de presupuesto efectivo por (categoria, mes) según scope actual.
  const scopedBudgets = useMemo(
    () => filterBudgetsByScope((budgetsAllQ.data ?? []) as any, isSolo, viewMode, profile?.id),
    [budgetsAllQ.data, isSolo, viewMode, profile?.id],
  );
  const budgetMatrix = useMemo(() => buildEffectiveBudgetMatrix(scopedBudgets as any, year), [scopedBudgets, year]);

  // Primer mes del año con presupuesto efectivo (mira los 12 meses, no se acota al actual).
  const autoStartMonth = useMemo(() => findYtdStartMonth(budgetMatrix), [budgetMatrix]);
  // Si el hogar definió un mes manual, sobrescribe el auto.
  const manualStartMonth = (householdQ.data as any)?.accounting_start_month as number | null | undefined;
  const ytdStartMonth = manualStartMonth ?? autoStartMonth;
  const isManualStart = manualStartMonth != null;
  // Presupuesto = proyección anual completa desde ytdStartMonth hasta diciembre.
  const ytdBudget = useMemo(
    () => (ytdStartMonth ? projectedAnnualBudget(budgetMatrix, ytdStartMonth) : 0),
    [budgetMatrix, ytdStartMonth],
  );

  // Gasto YTD (modo laxo): suma TODO el gasto entre ytdStartMonth y el mes
  // actual, sin filtrar por categorías con/sin presupuesto. Coincide con el
  // KPI de "Gastos del mes" cuando ytdStartMonth = mes actual.
  const ytdSpent = useMemo(() => {
    if (!ytdStartMonth) return 0;
    const regularYtd = ytdLines.reduce((s, l) => {
      const c = catMap.get(l.category_id ?? "");
      if (c?.type !== "expense") return s;
      if (adjustIds.has(l.category_id ?? "")) return s;
      const h = ytdHeaderById.get(l.header_id);
      if (!h) return s;
      const m = parseInt(h.date.slice(5, 7), 10);
      if (m < ytdStartMonth || m > month) return s;
      const sign = (h as any).is_refund ? -1 : 1;
      return s + sign * Number(l.amount);
    }, 0);
    const ccYtd = (ccYearQ.data?.purchases ?? []).reduce((s, p) => {
      const ch = ccYearChargesMap.get(p.id) ?? [];
      if (!ccPurchaseVisible(ch, viewMode, profile?.id, isSolo)) return s;
      const m = parseInt(p.purchase_date.slice(5, 7), 10);
      if (m < ytdStartMonth || m > month) return s;
      return s + Number(p.total_amount);
    }, 0);
    return regularYtd + ccYtd;
  }, [ytdLines, catMap, ytdHeaderById, ytdStartMonth, month, adjustIds, ccYearQ.data, ccYearChargesMap, viewMode, profile?.id, isSolo]);
  const ytdPct = ytdBudget > 0 ? (ytdSpent / ytdBudget) * 100 : 0;
  const ytdRemaining = Math.max(0, ytdBudget - ytdSpent);
  const ytdColor = ytdPct > 100 ? "bg-expense" : ytdPct > 80 ? "bg-warning" : "bg-income";

  // Presupuesto del mes actual: usa el efectivo (heredado si no hay fila exacta).
  const monthBudget = useMemo(() => totalEffectiveForMonth(budgetMatrix, month), [budgetMatrix, month]);
  // Gasto del mes actual (modo laxo) = mismo total que el KPI "Gastos del mes".
  const monthSpentForBudget = expenses;
  const monthPct = monthBudget > 0 ? (monthSpentForBudget / monthBudget) * 100 : 0;
  const monthRemaining = Math.max(0, monthBudget - monthSpentForBudget);
  const monthColor = monthPct > 100 ? "bg-expense" : monthPct > 80 ? "bg-warning" : "bg-income";
  const monthNames = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
  const ytdHint = ytdStartMonth
    ? `Inicio contable: ${monthNames[ytdStartMonth - 1]} (${isManualStart ? "manual" : "auto"}). Gastado: ${monthNames[ytdStartMonth - 1]} → ${monthNames[month - 1]} · Presupuesto: ${monthNames[ytdStartMonth - 1]} → diciembre.`
    : undefined;

  // Annual chart: gasto mensual vs presupuesto efectivo + acumulados.
  const annualData = useMemo(() => {
    const monthSpent = new Map<number, number>();
    const yearLines = yearTxQ.data?.lines ?? [];
    const yearHeaders = yearTxQ.data?.headers ?? [];
    const yearHeaderById = new Map(yearHeaders.map((h) => [h.id, h]));
    const visibleYearLines = isSolo
      ? yearLines
      : viewMode === "personal"
        ? yearLines.filter((l) => l.visibility === "private" && l.owner_id === profile?.id)
        : yearLines.filter((l) => l.visibility === "shared");
    visibleYearLines.forEach((l) => {
      const c = catMap.get(l.category_id ?? "");
      if (c?.type !== "expense") return;
      if (adjustIds.has(l.category_id ?? "")) return;
      const h = yearHeaderById.get(l.header_id);
      if (!h) return;
      const m = parseInt(h.date.slice(5, 7), 10);
      const sign = (h as any).is_refund ? -1 : 1;
      monthSpent.set(m, (monthSpent.get(m) ?? 0) + sign * Number(l.amount));
    });
    // Agregar compras CC agrupadas por mes de compra
    (ccYearQ.data?.purchases ?? []).forEach((p) => {
      const ch = ccYearChargesMap.get(p.id) ?? [];
      if (!ccPurchaseVisible(ch, viewMode, profile?.id, isSolo)) return;
      const m = parseInt(p.purchase_date.slice(5, 7), 10);
      monthSpent.set(m, (monthSpent.get(m) ?? 0) + Number(p.total_amount));
    });
    const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    let cumSpent = 0;
    let cumBudget = 0;
    return months.map((label, i) => {
      const m = i + 1;
      const spent = monthSpent.get(m) ?? 0;
      const budget = totalEffectiveForMonth(budgetMatrix, m);
      cumSpent += spent;
      cumBudget += budget;
      return { name: label, Presupuesto: budget, Gastado: spent, AcumGastado: cumSpent, AcumPresupuesto: cumBudget };
    });
  }, [budgetMatrix, yearTxQ.data, catMap, isSolo, viewMode, profile?.id, adjustIds, ccYearQ.data, ccYearChargesMap]);

  // Saldo proporcional con el hogar del mes que muestra el Dashboard (misma fuente que "Deudas del hogar").
  const fairShareMonth = formatMonthStart(year, month);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Mostrar totales en</span>
          <Select value={activeCurrency} onValueChange={setActiveCurrency}>
            <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Kpi title="Patrimonio Neto" value={formatCurrency(netWorth, activeCurrency)} icon={Wallet} />
        <Kpi title="Ingresos del mes" value={formatCurrency(income, activeCurrency)} icon={ArrowUpRight} positive />
        <Kpi title="Gastos del mes" value={formatCurrency(expenses, activeCurrency)} icon={ArrowDownRight} negative />
        <Kpi title="Deuda en tarjetas" value={formatCurrency(cardDebt, activeCurrency)} icon={TrendingUp} negative />
      </div>

      <AdjustmentsBanner net={adjustmentsNet.net} count={adjustmentsNet.count} currency={activeCurrency} />

      <UncategorizedBanner />

      {/* Pie + horizontal bars by category */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Gasto por categoría</CardTitle></CardHeader>
          <CardContent className="h-96">
            {expenseByCat.length === 0 ? <Empty msg="Sin gastos este mes" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 16, right: 80, bottom: 16, left: 80 }}>
                  <Pie
                    data={expenseByCat}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    labelLine
                    label={({ name, value, percent }) => {
                      if ((percent ?? 0) < 0.02) return "";
                      return `${name}: ${formatCurrency(Number(value), activeCurrency)} (${((percent ?? 0) * 100).toFixed(0)}%)`;
                    }}
                  >
                    {expenseByCat.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatCurrency(v, activeCurrency)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Comparativa por categoría</CardTitle></CardHeader>
          <CardContent className="h-96">
            {expenseByCat.length === 0 ? <Empty msg="Sin gastos este mes" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={expenseByCat} layout="vertical" margin={{ top: 8, right: 80, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => formatCurrency(Number(v), activeCurrency)} />
                  <YAxis type="category" dataKey="name" width={120} />
                  <Tooltip formatter={(v: number) => formatCurrency(v, activeCurrency)} />
                  <Bar dataKey="value">
                    {expenseByCat.map((e, i) => <Cell key={i} fill={e.color} />)}
                    <LabelList dataKey="value" position="right" formatter={(v: number) => formatCurrency(Number(v), activeCurrency)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* YTD progress */}
      <Card>
        <CardHeader><CardTitle>Presupuesto</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <BudgetHygieneBanner
            expenseByCat={expenseByCat}
            budgetMatrix={budgetMatrix}
            month={month}
            year={year}
            viewMode={viewMode}
          />
          <BudgetBar
            label="Presupuesto del mes actual"
            spent={monthSpentForBudget}
            budget={monthBudget}
            pct={monthPct}
            remaining={monthRemaining}
            color={monthColor}
            currency={activeCurrency}
            empty="No hay presupuesto cargado para este mes."
          />
          <BudgetBar
            label="Presupuesto anual proyectado"
            spent={ytdSpent}
            budget={ytdBudget}
            pct={ytdPct}
            remaining={ytdRemaining}
            color={ytdColor}
            currency={activeCurrency}
            empty="No hay presupuesto cargado para los meses del año en curso."
            hint={ytdHint}
          />
        </CardContent>
      </Card>

      {/* Annual control */}
      <Card>
        <CardHeader><CardTitle>Control anual {year}</CardTitle></CardHeader>
        <CardContent className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={annualData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v) => formatCurrency(Number(v), activeCurrency)} width={90} />
              <Tooltip formatter={(v: number) => formatCurrency(Number(v), activeCurrency)} />
              <Legend />
              <Bar dataKey="Presupuesto" fill="oklch(1 0 0 / 25%)" />
              <Bar dataKey="Gastado" fill="oklch(0.575 0.072 65.5)" />
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-2 text-xs text-muted-foreground">
            Comparativa mes a mes de presupuesto vs gasto.
          </p>
        </CardContent>
      </Card>

      {hh && profileId && members.length >= 2 && (
        <HouseholdFairShareSummary
          householdId={hh}
          profileId={profileId}
          members={members}
          month={fairShareMonth}
        />
      )}
      {inviteMove && (
        <InviteMoveDialog
          open
          targetHouseholdName={inviteMove.name}
          inviteCode={inviteMove.code}
          onClose={() => setInviteMove(null)}
        />
      )}
    </div>
  );
}

function Kpi({ title, value, icon: Icon, positive, negative }: { title: string; value: string; icon: React.ComponentType<{ className?: string }>; positive?: boolean; negative?: boolean }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">{title}</span>
          <Icon className={"h-4 w-4 " + (positive ? "text-income" : negative ? "text-expense" : "text-muted-foreground")} />
        </div>
        <div className="mt-2 text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function Empty({ msg }: { msg: string }) {
  return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{msg}</div>;
}

function BudgetBar({ label, spent, budget, pct, remaining, color, currency, empty, hint }: {
  label: string; spent: number; budget: number; pct: number; remaining: number; color: string; currency: string; empty: string; hint?: string;
}) {
  const formatCurrency = useFormatCurrency();
  if (budget === 0) {
    return (
      <div>
        <div className="text-sm font-medium">{label}</div>
        <p className="mt-1 text-sm text-muted-foreground">{empty}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xl font-bold">{pct.toFixed(0)}%</span>
      </div>
      <div className="relative h-4 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        {pct > 100 && (
          <div className="absolute inset-y-0 left-0 flex items-center px-2 text-[10px] font-bold text-white">
            +{(pct - 100).toFixed(0)}% sobre presupuesto
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 text-sm text-muted-foreground">
        <span>Gastado: <b className="text-foreground">{formatCurrency(spent, currency)}</b></span>
        <span>Presupuesto: <b className="text-foreground">{formatCurrency(budget, currency)}</b></span>
        <span>Restante: <b className={remaining > 0 ? "text-income" : "text-expense"}>{formatCurrency(remaining, currency)}</b></span>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
